/**
 * Date Range Filter Component
 * Reusable date range picker for analytics pages
 */

import { useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';

export interface DateRange {
  startDate: string; // ISO date string
  endDate: string; // ISO date string
  label: string; // "Last 7 days", "Last 30 days", etc.
}

interface DateRangeFilterProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  presets?: Array<{ label: string; days: number }>;
}

const DEFAULT_PRESETS = [
  { label: 'Today', days: 0 },
  { label: 'Last 2 days', days: 2 },
  { label: 'Last 3 days', days: 3 },
  { label: 'Last 5 days', days: 5 },
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 14 days', days: 14 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 60 days', days: 60 },
  { label: 'Last 90 days', days: 90 },
];

const DateRangeFilter = ({ value, onChange, presets = DEFAULT_PRESETS }: DateRangeFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Calculate date range from days ago
  const getDateRange = (days: number): DateRange => {
    const endDate = new Date();
    const startDate = new Date();

    if (days === 0) {
      // Today only
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
    } else {
      startDate.setDate(endDate.getDate() - days);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
    }

    const label = days === 0 ? 'Today' : `Last ${days} days`;

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      label,
    };
  };

  // Handle preset selection
  const handlePresetClick = (preset: { label: string; days: number }) => {
    const range = getDateRange(preset.days);
    onChange(range);
    setIsOpen(false);
    setCustomMode(false);
  };

  // Handle custom date range
  const handleCustomApply = () => {
    if (customStart && customEnd) {
      const start = new Date(customStart);
      const end = new Date(customEnd);

      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);

      onChange({
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        label: `${customStart} to ${customEnd}`,
      });

      setIsOpen(false);
      setCustomMode(false);
    }
  };

  // Format date for display
  const formatDateDisplay = (isoDate: string): string => {
    return new Date(isoDate).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="btn btn-secondary flex items-center gap-1.5 lg:gap-2 min-w-[140px] lg:min-w-[180px] text-xs lg:text-sm px-2 lg:px-4 py-2"
      >
        <Calendar className="w-3.5 h-3.5 lg:w-4 lg:h-4 shrink-0" />
        <span className="flex-1 text-left truncate">{value.label}</span>
        <ChevronDown
          className={`w-3.5 h-3.5 lg:w-4 lg:h-4 transition-transform shrink-0 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[100] bg-black/10 lg:bg-transparent backdrop-blur-[2px] lg:backdrop-blur-0"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown Content - Mobile/Tablet: bottom sheet, Desktop: dropdown */}
          <div
            className="
            fixed lg:absolute 
            left-2 right-2 bottom-2 
            lg:left-0 lg:right-auto lg:bottom-auto
            lg:mt-2 
            w-auto lg:w-80 
            max-h-[85vh] lg:max-h-[600px]
            bg-white dark:bg-secondary-800 
            rounded-2xl lg:rounded-lg 
            shadow-2xl 
            border border-secondary-200 dark:border-secondary-700 
            z-[200] 
            animate-slide-up lg:animate-slide-down
            flex flex-col
            overflow-hidden
          "
          >
            {!customMode ? (
              <>
                {/* Mobile/Tablet Header */}
                <div className="lg:hidden shrink-0 p-4 pb-2 border-b border-secondary-200 dark:border-secondary-700">
                  <h3 className="text-base font-semibold text-secondary-900 dark:text-secondary-100">
                    Select Date Range
                  </h3>
                </div>

                {/* Preset Options */}
                <div className="p-2 overflow-y-auto flex-1 min-h-0">
                  {presets.map((preset, index) => {
                    const isActive = value.label === preset.label;
                    return (
                      <button
                        key={index}
                        onClick={() => handlePresetClick(preset)}
                        className={`w-full px-3 lg:px-4 py-2.5 lg:py-2 rounded-lg text-left text-sm transition-all active:scale-[0.98]
                                  ${
                                    isActive
                                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 font-medium'
                                      : 'hover:bg-secondary-50 dark:hover:bg-secondary-700 active:bg-secondary-100 dark:active:bg-secondary-600 text-secondary-700 dark:text-secondary-300'
                                  }`}
                      >
                        {preset.label}
                      </button>
                    );
                  })}
                </div>

                {/* Custom Range Button */}
                <div className="border-t border-secondary-200 dark:border-secondary-700 p-2 shrink-0">
                  <button
                    onClick={() => setCustomMode(true)}
                    className="w-full px-3 lg:px-4 py-2.5 lg:py-2 rounded-lg text-left text-sm transition-all hover:bg-secondary-50 dark:hover:bg-secondary-700 active:bg-secondary-100 dark:active:bg-secondary-600 text-secondary-700 dark:text-secondary-300 active:scale-[0.98]"
                  >
                    Custom Range...
                  </button>
                </div>

                {/* Current Range Display */}
                <div className="border-t border-secondary-200 dark:border-secondary-700 p-3 bg-secondary-50 dark:bg-secondary-900 shrink-0">
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">
                    Current Range:
                  </p>
                  <p className="text-sm font-medium text-secondary-900 dark:text-secondary-100 break-words">
                    {formatDateDisplay(value.startDate)} - {formatDateDisplay(value.endDate)}
                  </p>
                </div>
              </>
            ) : (
              <>
                {/* Custom Date Picker */}
                <div className="p-4 overflow-y-auto flex-1">
                  <h3 className="text-base lg:text-sm font-semibold text-secondary-900 dark:text-secondary-100 mb-3">
                    Custom Date Range
                  </h3>

                  <div className="space-y-3">
                    <div>
                      <label className="label text-sm">Start Date</label>
                      <input
                        type="date"
                        value={customStart}
                        onChange={e => setCustomStart(e.target.value)}
                        className="input text-base lg:text-sm"
                        max={customEnd || new Date().toISOString().split('T')[0]}
                      />
                    </div>

                    <div>
                      <label className="label text-sm">End Date</label>
                      <input
                        type="date"
                        value={customEnd}
                        onChange={e => setCustomEnd(e.target.value)}
                        className="input text-base lg:text-sm"
                        min={customStart}
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </div>

                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => {
                        setCustomMode(false);
                        setCustomStart('');
                        setCustomEnd('');
                      }}
                      className="btn btn-secondary flex-1 text-sm py-2.5 active:scale-[0.98]"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCustomApply}
                      disabled={!customStart || !customEnd}
                      className="btn btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed text-sm py-2.5 active:scale-[0.98]"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DateRangeFilter;
